import React from 'react'
import {
  DocumentDuplicateIcon,
  HomeIcon,
  UsersIcon,
  HeartIcon,
  MapPinIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'

const DemographicsDashboard = ({ statistics, showDetails, toggleDetails }) => {
  const statistic = statistics.data
  const civilStatusStats = statistic.civil_status
  const purokStats = statistic.purok_distribution
  const addDemo = statistic.additional_demographics

  return (
    <div className='mb-8'>
      <div className='bg-white rounded-lg shadow mb-6'>
        <button
          onClick={toggleDetails}
          className='w-full px-6 py-4 flex items-center justify-between text-left  transition-all duration-200 rounded-lg'
        >
          <div className='flex items-center'>
            <DocumentDuplicateIcon className='w-6 h-6 mr-3 text-blue-600' />
            <div>
              <h4 className='text-lg font-semibold text-gray-900'>
                Demographics Dashboard
              </h4>
              <p className='text-sm text-gray-600'>
                Comprehensive population analytics and statistics
              </p>
            </div>
          </div>
          <div className='flex items-center space-x-2'>
            {showDetails ? (
              <ChevronUpIcon className='w-5 h-5' />
            ) : (
              <ChevronDownIcon className='w-5 h-5' />
            )}
          </div>
        </button>

        {showDetails && (
          <div className='space-y-6 p-4 animate-in slide-in-from-top duration-300'>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              <div className='bg-violet-50 rounded-lg shadow-sm p-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                  <HomeIcon className='w-5 h-5 mr-2 text-purple-600' />
                  Household Statistics
                </h3>
                <div className='space-y-1'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Total Households
                    </span>
                    <span className='text-md font-bold text-purple-600'>
                      {statistic.household_statistics.total_households}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Household Heads
                    </span>
                    <span className='text-md font-bold text-purple-600'>
                      {statistic.household_statistics.total_household_heads}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Family Members
                    </span>
                    <span className='text-md font-bold text-purple-600'>
                      {statistic.household_statistics.total_family_members}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Avg. Family Size
                    </span>
                    <span className='text-md font-bold text-purple-600'>
                      {statistic.household_statistics.average_family_size}
                    </span>
                  </div>
                </div>
              </div>

              <div className='bg-blue-50 rounded-lg shadow-sm p-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                  <UsersIcon className='w-5 h-5 mr-2 text-blue-600' />
                  Gender Distribution
                </h3>
                <div className='space-y-4'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>Male</span>
                    <div className='flex items-center space-x-2'>
                      <span className='text-md font-bold text-blue-600'>
                        {statistic.gender_distribution.male.total}
                      </span>
                      <span className='text-xs text-gray-500'>
                        ( {statistic.gender_distribution.male.percentage}
                        %)
                      </span>
                    </div>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-2'>
                    <div
                      className='bg-blue-600 h-2 rounded-full transition-all duration-500'
                      style={{
                        width: `${statistic.gender_distribution.male.percentage}%`,
                      }}
                    ></div>
                  </div>

                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>Female</span>
                    <div className='flex items-center space-x-2'>
                      <span className='text-md font-bold text-pink-600'>
                        {statistic.gender_distribution.female.total}
                      </span>
                      <span className='text-xs text-gray-500'>
                        ( {statistic.gender_distribution.female.percentage}
                        %)
                      </span>
                    </div>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-2'>
                    <div
                      className='bg-pink-600 h-2 rounded-full transition-all duration-500'
                      style={{
                        width: `${statistic.gender_distribution.female.percentage}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className='bg-green-50 rounded-lg  p-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                  <HeartIcon className='w-5 h-5 mr-2 text-green-600' />
                  Age Groups
                </h3>
                <div className='space-y-1'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Children (0-17)
                    </span>
                    <span className='text-md font-bold text-green-600'>
                      {statistic.age_groups.children}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>
                      Adults (18-59)
                    </span>
                    <span className='text-md font-bold text-yellow-600'>
                      {statistic.age_groups.adults}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>Seniors (60+)</span>
                    <span className='text-md font-bold text-red-600'>
                      {statistic.age_groups.seniors}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm text-gray-600'>PWD</span>
                    <span className='text-md font-bold text-orange-600'>
                      {statistic.age_groups.pwd}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <div className='bg-red-50 rounded-lg p-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                  <HeartIcon className='w-5 h-5 mr-2 text-red-600' />
                  Civil Status Distribution
                </h3>
                <div className='space-y-1'>
                  {Object.entries(civilStatusStats || {}).map(
                    ([status, data]) => (
                      <div
                        key={status}
                        className='flex justify-between items-center'
                      >
                        <span className='text-sm text-gray-600 capitalize'>
                          {status}
                        </span>

                        <div className='flex items-center space-x-2'>
                          <span className='text-md font-bold text-red-600'>
                            {data.total}
                          </span>
                          <span className='text-xs text-gray-500'>
                            ({data.percentage}%)
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className='bg-teal-50 rounded-lg shadow-sm p-6'>
                <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                  <MapPinIcon className='w-5 h-5 mr-2 text-teal-600' />
                  Purok/Zone Distribution
                </h3>

                <div className='space-y-1 max-h-48 overflow-y-auto'>
                  {purokStats?.length > 0 ? (
                    purokStats.map(({ purok, total, percentage }) => (
                      <div
                        key={purok}
                        className='flex justify-between items-center'
                      >
                        <span className='text-sm text-gray-600'>{purok}</span>

                        <div className='flex items-center space-x-2'>
                          <span className='text-md font-bold text-teal-600'>
                            {total}
                          </span>
                          <span className='text-xs text-gray-500'>
                            ({percentage}%)
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className='text-center text-gray-500 text-sm py-4'>
                      No purok/zone data available
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className='bg-white rounded-sm shadow-md p-6'>
              <h3 className='text-lg font-semibold text-gray-900 mb-4 flex items-center'>
                <DocumentDuplicateIcon className='w-5 h-5 mr-2 text-gray-600' />
                Additional Demographics Summary
              </h3>
              <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4'>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-blue-600'>
                    {addDemo.total_registered_voter}
                  </p>
                  <p className='text-xs text-gray-600'>Registered Voters</p>
                </div>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-green-600'>
                    {addDemo.total_employed}
                  </p>
                  <p className='text-xs text-gray-600'>Employed</p>
                </div>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-red-600'>
                    {addDemo.total_unemployed}
                  </p>
                  <p className='text-xs text-gray-600'>Unemployed</p>
                </div>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-purple-600'>
                    {addDemo.total_college_graduate}
                  </p>
                  <p className='text-xs text-gray-600'>College Graduates</p>
                </div>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-yellow-600'>
                    {addDemo.total_high_school_graduate}
                  </p>
                  <p className='text-xs text-gray-600'>High School Graduates</p>
                </div>
                <div className='text-center p-4 bg-gray-50 rounded-lg'>
                  <p className='text-2xl font-bold text-indigo-600'>
                    {addDemo.total_indigenous_groups}
                  </p>
                  <p className='text-xs text-gray-600'>Indigenous Groups</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DemographicsDashboard
